import React from 'react';

import { Modal, Table, Tag, Typography } from 'antd';
import { CheckCircleOutlined, WarningOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export default function ConfirmVerificationModal({ open, returnOrder, onConfirm, onCancel }) {
  const { t } = useTranslation();

  if (!returnOrder) return null;

  const items = returnOrder.items || [];
  const verifiedItems = items.filter(item => (item.received_quantity || 0) > 0);
  const unverifiedItems = items.filter(item => (item.received_quantity || 0) === 0 && (item.quantity || 0) > 0);

  const totalExpected = items.reduce((sum, i) => sum + (i.quantity || 0), 0);
  const totalReceived = verifiedItems.reduce((sum, i) => sum + (i.received_quantity || 0), 0);
  const unplannedCount = verifiedItems.filter(i => i.unplanned).length;
  const isMatch = totalReceived === totalExpected && unplannedCount === 0;

  const columns = [
    {
      title: t('operator.return_verification.confirm_modal.product', { defaultValue: '商品' }),
      key: 'product',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: 13 }}>{record.product?.name || '--'}</div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>{record.product?.sku}</div>
        </div>
      ),
    },
    {
      title: t('models.return_receipt_item.inventory_type'),
      key: 'inventory_type',
      width: 100,
      render: (_, record) => (
        <span style={{ fontSize: 13 }}>{record.inventory_type?.name || '-'}</span>
      ),
    },
    {
      title: t('operator.return_verification.confirm_modal.received_qty', { defaultValue: '實收' }),
      key: 'received',
      align: 'center',
      width: 60,
      render: (_, record) => (
        <Text strong style={{ fontSize: 15 }}>{record.received_quantity || 0}</Text>
      ),
    },
    {
      title: '',
      key: 'tags',
      width: 70,
      align: 'right',
      render: (_, record) => {
        if (record.unplanned) return <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>{t('operator.return_verification.unplanned', { defaultValue: '非預期' })}</Tag>;
        if (record.mismatch) return <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>{t('operator.return_verification.mismatch', { defaultValue: '變更' })}</Tag>;
        return null;
      },
    },
  ];

  return (
    <Modal
      title={null}
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      okText={t('operator.return_verification.confirm_modal.ok', { defaultValue: '確認驗收' })}
      cancelText={t('common.button.cancel', { defaultValue: '取消' })}
      width={560}
      centered
      styles={{ body: { padding: '0' } }}
    >
      {/* 數字摘要：實收 / 預期（含非預期附註）— 實際數放第一位以對齊 operator 心智模型「我收到多少 vs 應收多少」 */}
      <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', minWidth: 100 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>
              {t('operator.return_verification.confirm_modal.total_received', { defaultValue: '實收' })}
            </div>
            <div style={{
              fontSize: 40,
              fontWeight: 700,
              color: totalReceived === totalExpected ? '#52c41a' : '#ff4d4f',
              lineHeight: 1,
            }}>
              {totalReceived}
            </div>
            {unplannedCount > 0 && (
              <div style={{ marginTop: 6 }}>
                <Tag color="orange" style={{ fontSize: 11, margin: 0 }}>
                  {t('operator.return_verification.confirm_modal.includes_unplanned', {
                    defaultValue: '含 {{count}} 件非預期',
                    count: unplannedCount,
                  })}
                </Tag>
              </div>
            )}
          </div>
          <div style={{ textAlign: 'center', minWidth: 100 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>
              {t('operator.return_verification.confirm_modal.total_expected', { defaultValue: '預期' })}
            </div>
            <div style={{ fontSize: 40, fontWeight: 700, color: '#334155', lineHeight: 1 }}>
              {totalExpected}
            </div>
          </div>
        </div>
      </div>

      {/* 品項列表 */}
      <div style={{ padding: '16px 24px 8px' }}>
        {verifiedItems.length > 0 && (
          <Table
            dataSource={verifiedItems}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="id"
            style={{ marginBottom: 8 }}
          />
        )}

        {unverifiedItems.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px',
            background: '#fffbe6',
            borderRadius: 6,
            marginBottom: 8,
          }}>
            <WarningOutlined style={{ color: '#faad14', fontSize: 13 }} />
            <Text style={{ fontSize: 12, color: '#d48806' }}>
              {t('operator.return_verification.confirm_modal.unverified_warning', {
                defaultValue: '{{count}} 個預期品項尚未驗收',
                count: unverifiedItems.length
              })}
            </Text>
          </div>
        )}

        {verifiedItems.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 14 }}>
              {t('operator.return_verification.no_items_verified', { defaultValue: '沒有已驗收的品項' })}
            </Text>
          </div>
        )}
      </div>
    </Modal>
  );
}
